import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CreateTriggerOptions,
  DeleteTriggerByAppIdAndKeyOptions,
  GetDynamicSchemaResponse,
  GetIdentitiesSchemaResponse,
  GetTriggerByAppIdAndKeyIdentifiers,
  GetTriggerByAppIdAndKeyOptions,
  GetTriggerByAppIdAndKeyResponse,
  GetTriggerDynamicSchemaOptions,
  QueryTriggersResponse,
  ResolveTriggersOptions,
  ResolveTriggersResponse,
  ResultsQueryBuilder,
  Trigger,
  TriggerQuery,
  UpdateTriggerOptions,
  createTrigger as universalCreateTrigger,
  deleteTriggerByAppIdAndKey as universalDeleteTriggerByAppIdAndKey,
  getIdentitiesSchema as universalGetIdentitiesSchema,
  getTriggerByAppIdAndKey as universalGetTriggerByAppIdAndKey,
  getTriggerDynamicSchema as universalGetTriggerDynamicSchema,
  queryTriggers as universalQueryTriggers,
  resolveTriggers as universalResolveTriggers,
  typedQueryTriggers as universalTypedQueryTriggers,
  updateTrigger as universalUpdateTrigger,
} from './automations-triggercatalog-v1-trigger-trigger-catalog.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/automations' };

export function createTrigger(httpClient: HttpClient): CreateTriggerSignature {
  return (
    options?: NonNullablePaths<
      CreateTriggerOptions,
      | `trigger.appId`
      | `trigger.displayName`
      | `trigger.payloadDataSchema`
      | `trigger.triggerKey`,
      3
    >
  ) =>
    universalCreateTrigger(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateTriggerSignature {
  /**
   * Creates a trigger for the specified app. Once created, users can [create automations](https://support.wix.com/en/wix-automations-7067897) that run when this trigger occurs.
   *
   * Learn more about [triggers](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/about-triggers).
   *
   * > **Note:** You can only create triggers for [apps you own or collaborate on](https://dev.wix.com/docs/build-apps/develop-your-app/app-dashboard-setup/manage-collaborators).
   * @returns Created trigger.
   */
  (
    options?: NonNullablePaths<
      CreateTriggerOptions,
      | `trigger.appId`
      | `trigger.displayName`
      | `trigger.payloadDataSchema`
      | `trigger.triggerKey`,
      3
    >
  ): Promise<
    NonNullablePaths<
      Trigger,
      | `appId`
      | `triggerKey`
      | `displayName`
      | `filters`
      | `filters.${number}.staticFieldKey`
      | `filters.${number}.dynamicFieldKey.label`
      | `filters.${number}.dynamicFieldKey.entitySelector._id`
      | `filters.${number}.dynamicFieldKey.entitySelector.multiSelect`
      | `filters.${number}._id`
      | `filters.${number}.fieldKeyType`
      | `filters.${number}.valueInput.userInput.type`
      | `filters.${number}.valueInput.staticItems.multiSelect`
      | `filters.${number}.valueInput.type`
      | `filters.${number}.valueInput.label`
      | `filters.${number}.valueInput.required`
      | `filters.${number}.valueInput.reevaluateDynamicSchema`
      | `implementedMethods.validateConfiguration`
      | `implementedMethods.getDynamicSchema`,
      6
    >
  >;
}

export function updateTrigger(httpClient: HttpClient): UpdateTriggerSignature {
  return (
    _id: string,
    options?: NonNullablePaths<
      UpdateTriggerOptions,
      `trigger.appId` | `trigger.triggerKey`,
      3
    >
  ) =>
    universalUpdateTrigger(
      _id,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateTriggerSignature {
  /**
   * Updates the specified trigger for the specified app.
   *
   * After the trigger is updated, it only contains the values specified in the request. Earlier values are replaced.
   * @param - Trigger ID.
   * @returns Updated trigger.
   */
  (
    _id: string,
    options?: NonNullablePaths<
      UpdateTriggerOptions,
      `trigger.appId` | `trigger.triggerKey`,
      3
    >
  ): Promise<
    NonNullablePaths<
      Trigger,
      | `appId`
      | `triggerKey`
      | `displayName`
      | `filters`
      | `filters.${number}.staticFieldKey`
      | `filters.${number}.dynamicFieldKey.label`
      | `filters.${number}.dynamicFieldKey.entitySelector._id`
      | `filters.${number}.dynamicFieldKey.entitySelector.multiSelect`
      | `filters.${number}._id`
      | `filters.${number}.fieldKeyType`
      | `filters.${number}.valueInput.userInput.type`
      | `filters.${number}.valueInput.staticItems.multiSelect`
      | `filters.${number}.valueInput.type`
      | `filters.${number}.valueInput.label`
      | `filters.${number}.valueInput.required`
      | `filters.${number}.valueInput.reevaluateDynamicSchema`
      | `implementedMethods.validateConfiguration`
      | `implementedMethods.getDynamicSchema`,
      6
    >
  >;
}

export function deleteTriggerByAppIdAndKey(
  httpClient: HttpClient
): DeleteTriggerByAppIdAndKeySignature {
  return (
    triggerAppId: string,
    options: NonNullablePaths<
      DeleteTriggerByAppIdAndKeyOptions,
      `triggerKey`,
      2
    >
  ) =>
    universalDeleteTriggerByAppIdAndKey(
      triggerAppId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteTriggerByAppIdAndKeySignature {
  /**
   * Deletes a trigger from the specified app.
   *
   * > **Note**: [Automations that have the deleted trigger](https://support.wix.com/en/article/wix-automations-creating-an-automation-with-the-new-builder#step-2-choose-a-trigger) no longer run.
   * @param - ID of the [app that created the trigger](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/add-a-trigger-to-your-app).
   */
  (
    triggerAppId: string,
    options: NonNullablePaths<
      DeleteTriggerByAppIdAndKeyOptions,
      `triggerKey`,
      2
    >
  ): Promise<void>;
}

export function getTriggerByAppIdAndKey(
  httpClient: HttpClient
): GetTriggerByAppIdAndKeySignature {
  return (
    identifiers: NonNullablePaths<
      GetTriggerByAppIdAndKeyIdentifiers,
      `appId` | `triggerKey`,
      2
    >,
    options?: GetTriggerByAppIdAndKeyOptions
  ) =>
    universalGetTriggerByAppIdAndKey(
      identifiers,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetTriggerByAppIdAndKeySignature {
  /**
   * Retrieves the specified trigger.
   */
  (
    identifiers: NonNullablePaths<
      GetTriggerByAppIdAndKeyIdentifiers,
      `appId` | `triggerKey`,
      2
    >,
    options?: GetTriggerByAppIdAndKeyOptions
  ): Promise<
    NonNullablePaths<
      GetTriggerByAppIdAndKeyResponse,
      | `trigger.appId`
      | `trigger.triggerKey`
      | `trigger.displayName`
      | `trigger.filters`
      | `trigger.filters.${number}.staticFieldKey`
      | `trigger.filters.${number}.dynamicFieldKey.label`
      | `trigger.filters.${number}.dynamicFieldKey.entitySelector._id`
      | `trigger.filters.${number}.dynamicFieldKey.entitySelector.multiSelect`
      | `trigger.filters.${number}._id`
      | `trigger.filters.${number}.fieldKeyType`
      | `trigger.filters.${number}.valueInput.userInput.type`
      | `trigger.filters.${number}.valueInput.staticItems.multiSelect`
      | `trigger.filters.${number}.valueInput.type`
      | `trigger.filters.${number}.valueInput.label`
      | `trigger.filters.${number}.valueInput.required`
      | `trigger.filters.${number}.valueInput.reevaluateDynamicSchema`
      | `trigger.implementedMethods.validateConfiguration`
      | `trigger.implementedMethods.getDynamicSchema`,
      7
    >
  >;
}

export function queryTriggers(httpClient: HttpClient): QueryTriggersSignature {
  return () =>
    universalQueryTriggers(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryTriggersSignature {
  /**
   * Retrieves a list of triggers, given the provided paging, filtering, and sorting.
   *
   * To query all triggers by a particular app, specify its `appId` in the query's `filter` object.
   *
   *
   * Query Triggers runs with these defaults, which you can override:
   *
   * - `createdDate` is sorted in `DESC` order.
   * - `paging.limit` is `50`.
   * - `paging.offset` is `0`.
   *
   * To learn about working with query methods, see:
   * - [API Query Language](https://dev.wix.com/docs/api-reference/articles/getting-started/api-query-language)
   * - [Sorting and Paging](https://dev.wix.com/api/api-reference/getting-started/pagination)
   * - [Field projection](https://dev.wix.com/api/api-reference/getting-started/field-projection)
   */
  (): ResultsQueryBuilder;
}

export function typedQueryTriggers(
  httpClient: HttpClient
): TypedQueryTriggersSignature {
  return (query: TriggerQuery) =>
    universalTypedQueryTriggers(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryTriggersSignature {
  /** */
  (query: TriggerQuery): Promise<
    NonNullablePaths<
      QueryTriggersResponse,
      | `results`
      | `results.${number}.appId`
      | `results.${number}.triggerKey`
      | `results.${number}.displayName`
      | `results.${number}.implementedMethods.validateConfiguration`
      | `results.${number}.implementedMethods.getDynamicSchema`,
      5
    >
  >;
}

export function resolveTriggers(
  httpClient: HttpClient
): ResolveTriggersSignature {
  return (options?: ResolveTriggersOptions) =>
    universalResolveTriggers(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ResolveTriggersSignature {
  /**
   * Returns triggers that are installed on the site and have sufficient maturity.
   */
  (options?: ResolveTriggersOptions): Promise<
    NonNullablePaths<
      ResolveTriggersResponse,
      | `results`
      | `results.${number}.appId`
      | `results.${number}.triggerKey`
      | `results.${number}.displayName`
      | `results.${number}.implementedMethods.validateConfiguration`
      | `results.${number}.implementedMethods.getDynamicSchema`,
      5
    >
  >;
}

export function getTriggerDynamicSchema(
  httpClient: HttpClient
): GetTriggerDynamicSchemaSignature {
  return (
    appId: string,
    options?: NonNullablePaths<GetTriggerDynamicSchemaOptions, `triggerKey`, 2>
  ) =>
    universalGetTriggerDynamicSchema(
      appId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetTriggerDynamicSchemaSignature {
  /**
   * Retrieves a trigger's payload schema with the user's [filter field](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/automations/triggers/filter-fields) selections applied.
   *
   * Learn more about [the trigger payload schema](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/the-trigger-payload-schema).
   * @param - ID of the [app that created the trigger](https://dev.wix.com/docs/api-reference/business-management/automations/triggers/add-a-trigger-to-your-app).
   */
  (
    appId: string,
    options?: NonNullablePaths<GetTriggerDynamicSchemaOptions, `triggerKey`, 2>
  ): Promise<GetDynamicSchemaResponse>;
}

export function getIdentitiesSchema(
  httpClient: HttpClient
): GetIdentitiesSchemaSignature {
  return () =>
    universalGetIdentitiesSchema(
      // @ts-ignore
      { httpClient }
    );
}

interface GetIdentitiesSchemaSignature {
  /**
   * Returns trigger identities schema
   */
  (): Promise<GetIdentitiesSchemaResponse>;
}

export {
  AccountInfo,
  ActionEvent,
  BooleanInputOptions,
  BulkCreateWhitelistTriggerAppsRequest,
  BulkCreateWhitelistTriggerAppsResponse,
  BulkDeleteWhitelistTriggerAppsRequest,
  BulkDeleteWhitelistTriggerAppsResponse,
  CreateTriggerOptions,
  CreateTriggerRequest,
  CreateTriggerResponse,
  CursorPaging,
  Cursors,
  DebounceConfiguration,
  DeleteTriggerByAppIdAndKeyOptions,
  DeleteTriggerByAppIdAndKeyRequest,
  DeleteTriggerByAppIdAndKeyResponse,
  DeleteTriggerRequest,
  DeleteTriggerResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  DomainEventOptions,
  DynamicFieldKey,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntitySelector,
  EntityUpdatedEvent,
  ExperimentActionType,
  FieldKeyType,
  Filter,
  FilterFieldKeyOneOf,
  GetDynamicSchemaResponse,
  GetIdentitiesSchemaRequest,
  GetIdentitiesSchemaResponse,
  GetLatestTriggerRequest,
  GetLatestTriggerResponse,
  GetRuntimeTriggerRequest,
  GetRuntimeTriggerResponse,
  GetTriggerByAppIdAndKeyIdentifiers,
  GetTriggerByAppIdAndKeyOptions,
  GetTriggerByAppIdAndKeyRequest,
  GetTriggerByAppIdAndKeyResponse,
  GetTriggerDynamicSchemaOptions,
  GetTriggerDynamicSchemaRequest,
  GetTriggerRequest,
  GetTriggerResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ImplementedMethods,
  ListMarketplaceAndWhitelistTriggersRequest,
  ListMarketplaceAndWhitelistTriggersResponse,
  ListWhitelistTriggerAppsRequest,
  ListWhitelistTriggerAppsResponse,
  Maturity,
  MessageEnvelope,
  Metadata,
  MigrateComponentToTriggerRequest,
  MigrateComponentToTriggerResponse,
  NumberInputOptions,
  Paging,
  PagingMetadataV2,
  QueryRuntimeTriggersRequest,
  QueryRuntimeTriggersResponse,
  QueryTriggersRequest,
  QueryTriggersResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RemoveComponentDataRequest,
  RemoveComponentDataResponse,
  ResolveTriggersOptions,
  ResolveTriggersRequest,
  ResolveTriggersResponse,
  RestoreInfo,
  ResultsQueryBuilder,
  ResultsQueryResult,
  RetryExperimentCreation,
  Segment,
  SelectedFilterOptions,
  SortOrder,
  Sorting,
  Source,
  SourceOptionsOneOf,
  SourceType,
  StaticItemValue,
  StaticItems,
  TimeUnit,
  Trigger,
  TriggerQuerySpec,
  TriggerSpecInfo,
  Type,
  UpdateTriggerComponentRequest,
  UpdateTriggerComponentResponse,
  UpdateTriggerMaturityRequest,
  UpdateTriggerMaturityResponse,
  UpdateTriggerOptions,
  UpdateTriggerRequest,
  UpdateTriggerResponse,
  UserInput,
  UserInputInputOptionsOneOf,
  ValueInput,
  ValueInputInputOptionsOneOf,
  ValueInputType,
  WebhookIdentityType,
  utils,
} from './automations-triggercatalog-v1-trigger-trigger-catalog.universal.js';
